import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import { chPerm } from 'src/components/helpers';
import styled from '@emotion/styled';
import logo from '../../assets/static/comunidad_blanco.webp';
import { screenSizes } from 'src/assets/styles/Literals';

const SidebarContent = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 1000;
  background: #2596c2;
  color: white;
  border: none;
  border-radius: 50%;
  width: 45px;
  height: 45px;
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: #1e7ba8;
    transform: scale(1.05);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
  }
`;

const SidebarContainer = styled.div`
  width: 280px;
  background: #1d1d1b;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  color: white;
  z-index: 900;
  transition: transform 0.3s ease;
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(-100%)')};

  @media (max-width: ${screenSizes.tablet}) {
    width: 100%;
    max-width: 320px;
  }

  .sidebar-content {
    padding: 20px 0;
    .logo {
      padding: 0 0 0 25px;
    }
  }

  .category {
    margin-bottom: 10px;

    .category-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 20px;
      cursor: pointer;
      transition: background-color 0.2s ease;
      border-left: 3px solid transparent;

      &:hover {
        background-color: #333;
      }

      &.active {
        background-color: #444;
        border-left-color: #2596c2;
      }

      .category-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-weight: 500;
        font-size: 1rem;

        .icon {
          font-size: 1.1rem;
        }
      }

      .toggle-icon {
        transition: transform 0.2s ease;

        &.expanded {
          transform: rotate(90deg);
        }
      }
    }

    .category-links {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease;
      background-color: #262626;

      &.expanded {
        max-height: 500px;
      }

      .link-item {
        padding: 10px 20px 10px 50px;
        display: block;
        color: #ccc;
        text-decoration: none;
        transition: all 0.2s ease;
        border-left: 2px solid transparent;

        &:hover {
          background-color: #333;
          color: #fff;
          border-left-color: #2596c2;
        }

        &.active {
          background-color: #444;
          color: #2596c2;
          border-left-color: #2596c2;
        }

        .link-icon {
          margin-right: 8px;
          font-size: 0.9rem;
        }
      }
    }
  }

  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
    transition: all 0.3s ease;
  }
`;

const AdminSidebar = ({ isOpen, onToggle }) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const location = useLocation();

  // Auto-close sidebar when route changes
  React.useEffect(() => {
    if (isOpen) {
      onToggle();
    }
  }, [location.pathname]);

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const isLinkActive = (path) => {
    return location.pathname === path;
  };

  const adminLinks = [
    {
      id: 'crecer',
      title: 'Crecer',
      icon: 'folder',
      permission: ['crecer'],
      links: [
        {
          title: 'Fechas de crecer',
          path: '/admin/event-dates',
          icon: 'configure',
          permission: ['campus_rionegro'],
          inverse: true,
        },
        {
          title: 'Crecer Encuestas',
          path: '/admin/crecer/encuestas',
          icon: 'archive',
          permission: null,
        },
        {
          title: 'Inscripciones Relaciones Sanas',
          path: '/admin/relations-table',
          icon: 'users',
          permission: ['relations'],
        },
        {
          title: 'Reporte Crecer y Seminarios',
          path: '/admin/crecer/reports',
          icon: 'chart bar',
          permission: ['crecerAdmin', 'seminaryAdmin'],
        },
        {
          title: 'Generar QR autoregistro',
          path: '/admin/crecer/self-attendance',
          icon: 'qrcode',
          permission: ['crecerAdmin', 'seminaryAdmin'],
        },
      ],
    },
    {
      id: 'experience',
      title: 'Experiencia',
      icon: 'star',
      permission: [
        'Auditorio_PrincipalAdmin',
        'volunteers',
        'newMembers',
        'experienceCoord',
        'eventAdmin',
        'generaciones',
      ],
      links: [
        {
          title: 'Conexión',
          path: '/admin/new-members',
          icon: 'archive',
          permission: ['newMembers', 'experienceCoord'],
        },
        {
          title: 'Servicios y Eventos',
          path: '/admin/church-services',
          icon: 'archive',
          permission: ['experienceCoord', 'eventAdmin', 'generaciones'],
        },
        {
          title: 'Generaciones',
          path: '/admin/generaciones-services',
          icon: 'archive',
          permission: ['generaciones', 'experienceCoord'],
        },
        {
          title: 'Voluntarios Nuevos',
          path: '/admin/all-new-volunteers',
          icon: 'users',
          permission: ['volunteers'],
        },
        {
          title: 'Voluntarios',
          path: '/admin/all-volunteers',
          icon: 'users',
          permission: ['volunteers'],
        },
        {
          title: 'Voluntarios Confirmados',
          path: '/admin/all-confirmed-volunteers',
          icon: 'users',
          permission: ['volunteers'],
        },
      ],
    },
    {
      id: 'groups',
      title: 'Grupos',
      icon: 'users',
      permission: ['groups', 'relacionessanas'],
      links: [
        {
          title: 'Seminarios',
          path: '/admin/groups/Grupo_Seminario',
          icon: 'archive',
          permission: ['groups', 'relacionessanas'],
        },
        {
          title: 'Equipos',
          path: '/admin/groups/Equipo_de_Lideres',
          icon: 'archive',
          permission: ['groups'],
        },
        {
          title: 'Grupos',
          path: '/admin/groups/Grupo_Discipulado',
          icon: 'archive',
          permission: ['groups'],
        },
        {
          title: 'Aspirantes',
          path: '/admin/new-leaders',
          icon: 'archive',
          permission: null,
        },
        {
          title: 'Encuestas de Aspirantes',
          path: '/admin/prospect-leaders',
          icon: 'archive',
          permission: ['groupsAdmin'],
        },
      ],
    },

    {
      id: 'network',
      title: 'Social',
      permission: ['network', 'counseling'],
      icon: 'linkify',
      links: [
        {
          title: 'Red Empresas',
          path: '/admin/all-network',
          icon: 'archive',
          permission: ['network'],
        },
        {
          title: 'Consejería',
          path: '/admin/counseling',
          icon: 'archive',
          permission: ['counseling'],
        },
      ],
    },
    {
      id: 'discipulado',
      title: 'Discipulado',
      icon: 'book',
      permission: ['discipulado', 'solicitudes_matrimonio'],
      links: [
        {
          title: 'Solicitudes de matrimonio',
          path: '/admin/wedding-requests',
          icon: 'archive',
          permission: ['solicitudes_matrimonio'],
        },
        {
          title: 'Lideres y discípulos',
          path: '/admin/guest-leaders',
          icon: 'archive',
          permission: ['admin'],
        },
        {
          title: 'Acuerdos de Confidencialidad',
          path: '/admin/groups/compliances',
          icon: 'archive',
          permission: ['groups', 'relacionessanas'],
        },
      ],
    },
    {
      id: 'platform',
      title: 'Plataforma',
      icon: 'settings',
      permission: ['admin'],
      links: [
        {
          title: 'Buscar Personas',
          path: '/admin/all-guests',
          icon: 'archive',
          permission: ['admin'],
        },
        {
          title: 'Ver Usuarios',
          path: '/admin/all-users',
          icon: 'archive',
          permission: ['users'],
        },
      ],
    },
    {
      id: 'intranet',
      title: 'IntraComunidad',
      permission: ['intranet', 'intranetAdmin', 'staff', 'admin'],
      icon: 'share alternate',
      links: [
        {
          title: 'Tablero Gestión',
          path: '/intranet/asignaciones',
          icon: 'archive',
          permission: ['staff', 'intranet', 'intranetAdmin'],
        },
        {
          title: 'Tablero Estadisticas',
          path: '/tablero/crecer',
          icon: 'archive',
          permission: ['admin'],
        },
      ],
    },
  ];

  const hasPermission = (permissions, inverse = false) => {
    if (!permissions) return true;
    if (inverse) return !chPerm(permissions, true);
    return chPerm(permissions);
  };

  const shouldShowCategory = (category) => {
    return hasPermission(category.permission);
  };

  const shouldShowLink = (link) => {
    return hasPermission(link.permission, link.inverse);
  };

  return (
    <>
      <SidebarContent className="sidebar-toggle" onClick={onToggle}>
        {isOpen ? 'x' : '☰'}
      </SidebarContent>
      <div className="overlay" isOpen={isOpen} onClick={onToggle}></div>
      <SidebarContainer isOpen={isOpen}>
        <div className="sidebar-content">
          <div className="logo">
            <img className="header__img" src={logo} alt="Logo Comunidad" />
          </div>
          {adminLinks.map((category) => {
            if (!shouldShowCategory(category)) return null;

            const isExpanded = expandedCategories[category.id];
            const hasActiveLink = category.links.some(
              (link) => shouldShowLink(link) && isLinkActive(link.path)
            );

            return (
              <div key={category.id} className="category">
                <div
                  className={`category-header ${hasActiveLink ? 'active' : ''}`}
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="category-title">
                    <Icon name={category.icon} className="icon" />
                    {category.title}
                  </div>
                  <Icon
                    name="chevron right"
                    className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}
                  />
                </div>
                <div className={`category-links ${isExpanded ? 'expanded' : ''}`}>
                  {category.links.map((link, index) => {
                    if (!shouldShowLink(link)) return null;

                    return (
                      <Link
                        key={index}
                        to={link.path}
                        className={`link-item ${isLinkActive(link.path) ? 'active' : ''}`}
                      >
                        <Icon name={link.icon} className="link-icon" />
                        {link.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </SidebarContainer>
    </>
  );
};

export default AdminSidebar;
